import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";


export type ReportFilterType = 'string' | 'integer' | 'date' | 'today_date' | 'boolean' | 'select' | 'location_multiselect';

export interface ReportFilterOption {
    value: string | number;
    label: string;
    [key: string]: unknown;
}

export interface ReportFilter {
    name: string;
    type: ReportFilterType;
    value?: string | number | boolean | null;
    query?: string | null;
    label?: string;
    is_required?: boolean;
    options_type?: string | null;
    options?: ReportFilterOption[];
    [key: string]: unknown;
}

export interface ItReportTemplateLanguage {
    id: number;
    language?: string | null;
    display_name?: string | null;
    code?: string | null;
    [key: string]: unknown;
}

export interface ItReportTemplateResponse {
    id?: number;
    name?: string;
    sql_query?: string;
    jrxml_content_hash?: string;
    report_uuid?: string;
    parameters?: ReportFilter[];
    category_id?: number | null;
    category_name?: string | null;
    uploaded_languages?: number[];
    languages?: ItReportTemplateLanguage[];
    [key: string]: unknown;
}

export interface UploadReportRequest {
    id?: number;
    name: string;
    sql_query: string;
    jrxml_content_hash?: string;
    parameters: ReportFilter[];
    category_id?: number | null;
    [key: string]: unknown;
}

export interface ItReportParamTypeResponse {
    id?: number;
    name?: string;
    label?: string;
    type?: ReportFilterType;
    sql_query?: string;
    is_required?: number | boolean;
    default_value?: string | number | boolean | null;
    [key: string]: unknown;
}

export interface ItReportCategoryResponse {
    id?: number;
    name?: string;
    [key: string]: unknown;
}

type UploadJrxmlResponse = {
    report_uuid?: string;
    message?: string;
    [key: string]: unknown;
};

function formatErrorDetail(detail: unknown): string {
    if (!detail) {
        return '';
    }

    if (typeof detail === 'string') {
        return detail;
    }

    if (typeof detail === 'object') {
        const errorDetail = detail as {
            message?: unknown;
            url?: unknown;
            status_code?: unknown;
            response?: unknown;
        };
        const parts = [
            typeof errorDetail.message === 'string' ? errorDetail.message : '',
            errorDetail.status_code ? `status ${errorDetail.status_code}` : '',
            typeof errorDetail.url === 'string' ? errorDetail.url : '',
            typeof errorDetail.response === 'string' ? errorDetail.response : '',
        ].filter(Boolean);

        return parts.length > 0 ? parts.join(' - ') : JSON.stringify(detail);
    }

    return String(detail);
}


export async function uploadReport(uploadReportRequest: UploadReportRequest, jrxmlFile?: File): Promise<ItReportTemplateResponse> {
    try {
        // Step 1: Create/Update report in database (gets UUID)
        const reportResponse: ApiResponse<ItReportTemplateResponse> = (
            await apiClient.post('/report/upload_report',
                {
                    ...uploadReportRequest,
                    jrxml_content_hash: ""  // Not used in new workflow
                }
            )).data;

        const reportData = reportResponse.data;
        const reportUuid = reportData.report_uuid;

        // Step 2: If JRXML file provided, upload it using the UUID
        if (jrxmlFile) {
            try {
                await uploadJrxmlWithUuid(reportUuid!, 'en', jrxmlFile);
            } catch (error) {
                // Don't throw - report was created successfully, just JRXML upload failed
                // User can retry uploading the file using upload_jrxml endpoint
            }
        }

        return reportData;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Creating report failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function uploadJrxml(file: File): Promise<UploadJrxmlResponse> {
    try {
        const formData = new FormData();
        formData.append("file", file);
        const response: ApiResponse<UploadJrxmlResponse> = (
            await apiClient.post('/report/upload_jrxml',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            )).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Uploading jrxml failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function uploadJrxmlWithUuid(reportUuid: string, languageCode: string, file: File): Promise<UploadJrxmlResponse> {
    try {
        const formData = new FormData();
        formData.append("file", file);

        const response: ApiResponse<UploadJrxmlResponse> = (
            await apiClient.post(`/report/upload_jrxml/${reportUuid}?language_code=${languageCode}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            )).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || `Uploading ${languageCode} jrxml failed`);
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getReportTemplateById(templateId: number): Promise<ItReportTemplateResponse> {
    try {
        const response: ApiResponse<ItReportTemplateResponse> = (
            await apiClient.get(`/report/template/${templateId}`)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching report template failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function downloadReport(
    reportId: number,
    parameters: { [key: string]: any },
    fileFormat: 'pdf' | 'html',
    languageCode?: string,
    parameterLabels?: { [key: string]: string }
): Promise<string | void> {
    try {
        const response = await apiClient.post<ArrayBuffer>(
            '/report/download_report',
            {
                report_id: reportId,
                parameters,
                parameter_labels: parameterLabels || {},  // Send selected option labels
                file_format: fileFormat,
                language_code: languageCode || 'en'  // Default to English code 'en' if not provided
            },
            {
                responseType: 'arraybuffer'
            }
        );

        if (fileFormat === 'pdf') {
            // Download PDF file
            await streamPdfDownload(response.data);
            return undefined;
        } else if (fileFormat === 'html') {
            // Return HTML for display
            const html = await streamHtmlDisplay(response.data);
            return html;
        }
    } catch (error: any) {
        //eslint-disable-next-line no-console
        let errorDetail = '';
        if (error.response && error.response.data) {
            try {
                const decoder = new TextDecoder();
                const text = decoder.decode(error.response.data);
                const json = JSON.parse(text);
                errorDetail = formatErrorDetail(json.detail) || text;
            } catch {
                errorDetail = 'Failed to decode error response';
            }
            throw new Error(errorDetail);
        }
        throw new Error(`Downloading report failed: ${error.message}`);
    }
}

async function streamPdfDownload(arrayBuffer: ArrayBuffer): Promise<void> {
    const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `report-${new Date().getTime()}.pdf`;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
}

async function streamHtmlDisplay(arrayBuffer: ArrayBuffer): Promise<string> {
    const decoder = new TextDecoder();
    const html = decoder.decode(arrayBuffer);
    return html;
}

export async function getReportParamTypes(): Promise<ItReportParamTypeResponse[]> {
    try {
        const response: ApiResponse<ItReportParamTypeResponse[]> = (
            await apiClient.get('/report/parameters_types')
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching report parameter types failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getReportTemplateCategories(): Promise<ItReportCategoryResponse[]> {
    try {
        const response: ApiResponse<ItReportCategoryResponse[]> = (
            await apiClient.get('/report/template_categories')
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching report template categories failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getAllReportTemplates(): Promise<ItReportTemplateResponse[]> {
    try {
        const response: ApiResponse<ItReportTemplateResponse[]> = (
            await apiClient.get('/report/list_reports')
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching report templates failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getUserReports(userId: number): Promise<number[]> {
    try {
        const response: ApiResponse<number[]> = (
            await apiClient.get(`/users/${userId}/reports`)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error('Failed to fetch user reports');
    }
}

export async function getReportLanguages(reportId: number): Promise<number[]> {
    // Get list of languages that have been uploaded for a report.
    // reportId: ID of the report template
    // Returns: Array of language IDs that have been uploaded, e.g., [1, 2, 3]
    try {
        const response: ApiResponse<{ report_id: number; english_content_hash: string; uploaded_languages: number[] }> = (
            await apiClient.get(`/report/${reportId}/languages`)
        ).data;
        return response.data.uploaded_languages;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error('Failed to fetch report languages');
    }
}

export async function getReportListForCurrentUser() {
    try {
        const response: ApiResponse<ItReportTemplateResponse[]> = (
            await apiClient.get('/report/list_reports/current_user')
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error('Failed to fetch reports for current user');
    }

}
export async function deleteReport(reportId: number): Promise<{ report_id: number }> {
    try {
        const response: ApiResponse<{ report_id: number }> = (
            await apiClient.delete(`/report/${reportId}`)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error('Failed to delete report');
    }
}
